import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createPoolOrder,
  purchasePoolOrder,
  type ApiClient,
  type PoolOfferDetail,
} from '../../api';
import { gameEvents } from '../../postMessage';
import { useWallet } from '../../wallet';
import { useGameFlow, type GameState } from './useGameFlow';

/**
 * `useGamePurchase` — single composed hook driving the buy-flow on a
 * Pack page. Classic and Modern both consume this so the orchestration
 * lives in exactly one place:
 *
 *   1. Owns `useGameFlow` (idle | purchasing | failed).
 *   2. Drives the create-order → purchase API sequence, then hands off
 *      to <RevealScreen> via `onRevealReady` (host parks the new
 *      `order_id` in URL state and the reveal screen mounts).
 *   3. Refreshes the wallet on completion.
 *   4. Implements the "quick-buy" queue: tapping "Rip Repackz" on a
 *      Just Pulled card calls `queueRip(offerId)`, which switches
 *      `quantity` to 1 and stashes the requested offer id; an internal
 *      effect then fires the purchase once the new offer has finished
 *      loading and is in an `available` state.
 *   5. Exposes `buy()` with the standard busy + status guards so
 *      consumers can wire it directly to a button's `onClick`.
 */

export interface UseGamePurchaseArgs {
  client: ApiClient;
  /** The offer id (URL game id or slider's active offer). Empty string
   *  is treated as "no offer selected yet" — purchase calls become no-ops. */
  offerId: string;
  /** The loaded offer detail used for status guards. `null` while loading. */
  offer: PoolOfferDetail | null;
  /** Current quantity from the BottomAction stepper. */
  quantity: number;
  /** Quantity setter — `queueRip` resets it to 1 because Just Pulled
   *  cards don't expose a stepper. */
  setQuantity: (next: number) => void;
  /** Called with the new `order_id` after a successful purchase. Hosts
   *  use this to park the id in URL state and trigger the reveal screen. */
  onRevealReady: (orderId: string) => void;
}

export interface UseGamePurchaseApi {
  /** Wire to the main RIP! button. Includes busy + status guards. */
  buy: () => void;
  /** Wire to Just Pulled "Rip Repackz" taps. Caller is still
   *  responsible for switching the active offer + URL; this hook only
   *  queues the purchase. */
  queueRip: (offerId: number) => void;
  /** Wire to PurchaseOverlay's onRetry. Resets flow and re-fires. */
  retry: () => void;
  /** True while the flow is anything other than `idle`. */
  isBusy: boolean;
  /** Pass-through to PurchaseOverlay's `state` prop. */
  flow: GameState;
  /** Pass-through reset — consumers wire it to PurchaseOverlay's
   *  `onClose` (closes the failure screen). */
  resetFlow: () => void;
}

const AVAILABLE_STATUS = 'available';
function isOfferAvailable(status: string | null | undefined): boolean {
  return (status ?? AVAILABLE_STATUS) === AVAILABLE_STATUS;
}

export function useGamePurchase({
  client,
  offerId,
  offer,
  quantity,
  setQuantity,
  onRevealReady,
}: UseGamePurchaseArgs): UseGamePurchaseApi {
  const wallet = useWallet();
  const flow = useGameFlow();
  // True while `run()` has an outstanding fetch — independent of the
  // visible flow state so `retry()` (which calls `flow.reset()` first)
  // can't slip a second run in next to an in-flight first one.
  const inFlightRef = useRef(false);
  // Holds the current run's AbortController so an unmount cancels the
  // in-flight createPoolOrder / purchasePoolOrder fetch instead of
  // letting it land on a stale closure.
  const abortRef = useRef<AbortController | null>(null);

  // Cancel any in-flight purchase on unmount. Network responses that
  // arrive after we've left the page would otherwise set flow state on
  // a torn-down hook and (worse) emit purchase_completed for an order
  // the host can no longer route into reveal.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
      inFlightRef.current = false;
    };
  }, []);

  // Full buy flow: create pool_order → purchase → hand off to reveal.
  // UI shows the purchasing spinner while the API round-trip runs; on
  // success we close the overlay and the consumer mounts <RevealScreen>
  // via `onRevealReady`. Emits postMessage so the parent window can
  // track progress.
  const run = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;

    // Move UI out of 'idle' immediately so the buy button disappears
    // even when the backend createPoolOrder call is slow (dev proxy can
    // take seconds). Real orderId replaces 'pending' after create.
    flow.startPurchase('pending');
    let orderId: string | null = null;
    try {
      const order = await createPoolOrder(client, offerId, quantity);
      if (controller.signal.aborted) return;
      orderId = order.id;
      flow.startPurchase(order.id);
      gameEvents.purchaseInitiated(order.id);

      const result = await purchasePoolOrder(client, order.id);
      if (controller.signal.aborted) return;
      gameEvents.purchaseCompleted(
        order.id,
        result.items.map((i) => ({ id: i.id, name: i.name, rarity: i.rarity })),
      );
      flow.reset();
      void wallet.refresh();
      onRevealReady(order.id);
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : 'unknown';
      gameEvents.purchaseFailed(orderId, message);
      flow.fail(message);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      inFlightRef.current = false;
    }
  }, [client, offerId, quantity, flow, wallet, onRevealReady]);

  const [pendingRipOfferId, setPendingRipOfferId] = useState<number | null>(null);
  const isBusy = flow.state.step !== 'idle';

  // Fire the queued purchase from a Just Pulled "Rip Repackz" tap once
  // the new offer has finished loading. Same gating as the on-page RIP
  // button: status check, busy guard, then `run()`.
  useEffect(() => {
    if (pendingRipOfferId === null) return;
    if (offerId !== String(pendingRipOfferId)) return;
    if (!offer) return;
    if (!isOfferAvailable(offer.status)) {
      setPendingRipOfferId(null);
      return;
    }
    if (flow.state.step !== 'idle') return;
    setPendingRipOfferId(null);
    void run();
  }, [pendingRipOfferId, offerId, offer, flow.state.step, run]);

  const buy = useCallback(() => {
    if (isBusy || inFlightRef.current) return;
    if (offer && !isOfferAvailable(offer.status)) return;
    void run();
  }, [isBusy, offer, run]);

  const queueRip = useCallback(
    (id: number) => {
      setQuantity(1);
      setPendingRipOfferId(id);
    },
    [setQuantity],
  );

  // Only meaningful from the FailureScreen — guard so a rapid double-tap
  // can't slip a second `run()` in past the inFlightRef check.
  const retry = useCallback(() => {
    if (inFlightRef.current) return;
    if (flow.state.step !== 'failed') return;
    flow.reset();
    void run();
  }, [flow, run]);

  return {
    buy,
    queueRip,
    retry,
    isBusy,
    flow: flow.state,
    resetFlow: flow.reset,
  };
}
